import { Box, Loader, Paper, Text } from "@mantine/core";
import { useFeature } from "flagged";
import type { Data, Layout } from "plotly.js";
import Plotly from "plotly.js-dist-min";
import { useCallback, useEffect, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import type { Reference } from "src/db";
import { useArtifactHandler } from "../utils/artifactHandler";

const Plot = createPlotlyComponent(Plotly);

interface TimeseriesPlotProps {
    reference: Reference;
}

type SeriesTrace = {
    name: string;
    x: string[];
    y: (number | null)[];
    unit?: string;
};

const formatMetricValue = (value: number): string => {
    if (Number.isInteger(value)) {
        return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
};

const parseTimeseriesCsv = (text: string): SeriesTrace[] => {
    const lines = text
        .trim()
        .split(/\r?\n/)
        .filter((line) => line.length > 0);
    if (lines.length < 2) {
        return [];
    }

    const bySeries = new Map<string, SeriesTrace>();
    for (const line of lines.slice(1)) {
        // series,timestamp,value,unit — unit may contain commas rarely; keep simple split
        const parts = line.split(",");
        if (parts.length < 3) {
            continue;
        }
        const [name, timestamp, rawValue, ...unitParts] = parts;
        const unit = unitParts.join(",") || undefined;
        const value =
            rawValue === "" || rawValue === undefined ? null : Number.parseFloat(rawValue);
        let trace = bySeries.get(name);
        if (!trace) {
            trace = { name, x: [], y: [], unit };
            bySeries.set(name, trace);
        }
        trace.x.push(timestamp);
        trace.y.push(Number.isFinite(value as number) ? (value as number) : null);
    }
    return Array.from(bySeries.values());
};

const buildFigureFromTraces = (
    traces: SeriesTrace[],
    title?: string,
): { data: Data[]; layout: Partial<Layout> } => {
    const units = traces.map((t) => t.unit).filter(Boolean) as string[];
    const yTitle = units.length === 1 ? units[0] : "Value";
    return {
        data: traces.map((trace) => ({
            type: "scatter",
            mode: "lines",
            name: trace.name,
            x: trace.x,
            y: trace.y,
        })),
        layout: {
            title: title ? { text: title } : undefined,
            xaxis: { title: { text: "Time" } },
            yaxis: { title: { text: yTitle } },
            legend: { orientation: "h" },
            margin: { l: 60, r: 20, t: 50, b: 50 },
        },
    };
};

export const TimeseriesPlot = ({ reference }: TimeseriesPlotProps) => {
    const { getArtifactBlob } = useArtifactHandler();
    const [csvFigure, setCsvFigure] = useState<{
        data: Data[];
        layout: Partial<Layout>;
    } | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isTimeseriesCsv =
        reference.dataSourceType === "timeseries" ||
        (Boolean(reference.source?.includes("v1/storage/")) &&
            reference.source.toLowerCase().includes(".csv"));

    useEffect(() => {
        if (!isTimeseriesCsv || !reference.source) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setCsvFigure(null);

        (async () => {
            const blob = await getArtifactBlob(reference.source);
            if (cancelled) {
                return;
            }
            if (!blob) {
                setError("Could not load timeseries data");
                setLoading(false);
                return;
            }
            try {
                const text = await blob.text();
                const traces = parseTimeseriesCsv(text);
                if (!traces.length) {
                    setError("No timeseries points in CSV");
                    setLoading(false);
                    return;
                }
                setCsvFigure(buildFigureFromTraces(traces, reference.title));
            } catch (err) {
                console.error("Failed to parse timeseries CSV", err);
                setError("Failed to parse timeseries CSV");
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [getArtifactBlob, isTimeseriesCsv, reference.source, reference.title]);

    const metric = reference.metric;
    if (
        reference.displayMode === "metric" ||
        (metric && !reference.plotlyFigure && !isTimeseriesCsv)
    ) {
        if (!metric) {
            return null;
        }
        const label = metric.label || reference.title || "Value";
        return (
            <Paper
                withBorder
                radius="md"
                p="lg"
                mt="md"
                maw={360}
                data-testid={`timeseries-metric-${reference.reference_id}`}
            >
                <Text size="sm" c="dimmed" mb={4}>
                    {label}
                </Text>
                <Text size="2rem" fw={700} lh={1.2}>
                    {formatMetricValue(metric.value)}
                    {metric.unit ? (
                        <Text span size="md" fw={500} c="dimmed" ml={6}>
                            {metric.unit}
                        </Text>
                    ) : null}
                </Text>
                {metric.timestamp ? (
                    <Text size="xs" c="dimmed" mt="sm">
                        {metric.timestamp}
                    </Text>
                ) : null}
            </Paper>
        );
    }

    if (isTimeseriesCsv) {
        if (loading) {
            return (
                <Box mt="sm" data-testid={`timeseries-plot-loading-${reference.reference_id}`}>
                    <Loader size="sm" />
                </Box>
            );
        }
        if (error) {
            return (
                <Text size="sm" c="dimmed" mt="sm">
                    {error}
                </Text>
            );
        }
        if (!csvFigure?.data?.length) {
            return null;
        }
        return (
            <Box mt="sm" data-testid={`timeseries-plot-${reference.reference_id}`}>
                {reference.title ? (
                    <Text size="sm" fw={600} mb="xs">
                        {reference.title}
                    </Text>
                ) : null}
                <Plot
                    data={csvFigure.data}
                    layout={{
                        autosize: true,
                        ...csvFigure.layout,
                        margin: {
                            l: 60,
                            r: 20,
                            t: 40,
                            b: 50,
                            ...(csvFigure.layout.margin ?? {}),
                        },
                    }}
                    config={{ responsive: true, displaylogo: false }}
                    style={{ width: "100%", minHeight: 320 }}
                    useResizeHandler
                />
            </Box>
        );
    }

    const figure = reference.plotlyFigure;
    if (!figure?.data?.length) {
        return null;
    }

    const layout = (figure.layout ?? {}) as Partial<Layout>;

    return (
        <Box mt="sm" data-testid={`timeseries-plot-${reference.reference_id}`}>
            {reference.title ? (
                <Text size="sm" fw={600} mb="xs">
                    {reference.title}
                </Text>
            ) : null}
            <Plot
                data={figure.data as Data[]}
                layout={{
                    autosize: true,
                    ...layout,
                    margin: {
                        l: 60,
                        r: 20,
                        t: 40,
                        b: 50,
                        ...(layout.margin ?? {}),
                    },
                }}
                config={{ responsive: true, displaylogo: false }}
                style={{ width: "100%", minHeight: 320 }}
                useResizeHandler
            />
        </Box>
    );
};

export const isChartReference = (ref: Reference): boolean =>
    ref.dataSourceType === "timeseries" ||
    ref.dataSourceType === "plotly" ||
    Boolean(ref.plotlyFigure) ||
    Boolean(ref.metric);

/** Feature flag gating inline Plotly/timeseries charts (enabled on GS dev and qa only). */
export const PLOTLY_CHARTS_FEATURE = "PLOTLY_CHARTS";

/**
 * Chart-reference predicate honoring the PLOTLY_CHARTS flag. When the flag is off,
 * chart references are treated as ordinary citations (no plot, listed under Sources).
 */
export const useIsChartReference = () => {
    const enabled = Boolean(useFeature(PLOTLY_CHARTS_FEATURE));
    return useCallback((ref: Reference) => enabled && isChartReference(ref), [enabled]);
};
